import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import CheckInButton from '../CheckInButton';
import api from '../../utils/api';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, 
  UserCircle, 
  FileText, 
  Phone, 
  FileCheck, 
  CreditCard, 
  Settings, 
  LayoutDashboard, 
  LogOut, 
  Menu, 
  X,
  ChevronDown,
  ChevronRight,
  Award,
  Shield,
  Clock,
  CalendarClock,
  Building2,
  Tag,
  FolderTree,
  Percent,
  Truck,
  Package,
  ShoppingCart,
  Warehouse,
  Receipt,
  Wallet,
  BookOpen,
  IndianRupee,
  PieChart,
  MessageSquareWarning,
  Key,
  CloudUpload,
  ClipboardCheck,
  Search,
  RotateCcw,
  BarChart3
} from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Layout = () => {
  const { user, logout, permissions, hasPermission, hasAnyPermissionInModule, refreshPermissions } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [businessInfo, setBusinessInfo] = useState(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  
  // Change password state
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // Fetch business info
  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/business-settings/public`);
        setBusinessInfo(response.data);
      } catch (error) {
        setBusinessInfo({
          business_name: 'SUNTECH ENTERPRISES, Coimbatore',
          logo_url: null
        });
      }
    };
    fetchBusinessInfo();
  }, []);

  // Handle permissions loading with retry logic
  useEffect(() => {
    if (permissions && Object.keys(permissions).length > 0) {
      setPermissionsLoaded(true);
      console.log('Layout: Permissions loaded', permissions);
    } else if (user && !permissionsLoaded) {
      console.log('Layout: Permissions not yet loaded, refreshing...');
      // Retry fetching permissions if not loaded
      const timer = setTimeout(() => {
        if (refreshPermissions) {
          refreshPermissions();
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [permissions, user, permissionsLoaded, refreshPermissions]);
  
  // Auto-collapse sidebar when navigating to any module (except dashboard)
  // This effect is intentional - we need to sync sidebar state with navigation
  useEffect(() => {
    if (location.pathname !== '/dashboard') {
      setSidebarCollapsed(true);
    }
  }, [location.pathname]);

  // Get initials from business name
  const getInitials = (name) => {
    if (!name) return 'ST';
    const words = name.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current_password) {
      toast.error('Please enter your current password');
      return;
    }
    if (!passwordForm.new_password) {
      toast.error('Please enter a new password');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    setChangingPassword(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password changed successfully');
      setChangePasswordOpen(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Get isAdmin flag
  const isAdmin = user?.role === 'Admin';

  // Define navigation items based on permissions
  const getNavigationItems = () => {
    // All possible menu items with their permission mappings
    // parentPermission: If user has permission on parent, they can access child
    const allItems = [
      { 
        name: 'Dashboard', 
        icon: LayoutDashboard, 
        path: '/dashboard', 
        permissionId: 'main_dashboard',
        parentPermission: 'dashboard',
        alwaysShow: true // Dashboard always visible
      },
      { 
        name: 'Owner Dashboard', 
        icon: PieChart, 
        path: '/owner-dashboard', 
        permissionId: 'owner_dashboard',
        parentPermission: 'dashboard'
      },
      { 
        name: 'Reports', 
        icon: BarChart3, 
        path: '/reports', 
        permissionId: 'reports_admin_only',
        parentPermission: 'dashboard'
      },
      { 
        name: 'Customers', 
        icon: UserCircle, 
        path: '/customers', 
        permissionId: 'customers',
        parentPermission: 'crm'
      },
      { 
        name: 'Leads', 
        icon: Phone, 
        path: '/leads', 
        permissionId: 'leads',
        parentPermission: 'crm'
      },
      { 
        name: 'Services', 
        icon: FileCheck, 
        path: '/services', 
        permissionId: 'service_management',
        parentPermission: 'services'
      },
      { 
        name: 'Service Approvals', 
        icon: ClipboardCheck, 
        path: '/service-approvals', 
        permissionId: 'service_approval',
        parentPermission: 'services'
      },
      { 
        name: 'Complaints', 
        icon: MessageSquareWarning, 
        path: '/complaints', 
        permissionId: 'complaints',
        parentPermission: 'services'
      },
      { 
        name: 'Sales & Billing', 
        icon: IndianRupee, 
        permissionId: 'sales_billing',
        parentPermission: 'sales',
        subPermissions: ['sales_billing'],
        subItems: [
          { name: 'Sales & Billing', icon: IndianRupee, path: '/sales-billing', permissionId: 'sales_billing', parentPermission: 'sales' },
          { name: 'Payment Collections', icon: Wallet, path: '/payment-collections', permissionId: 'sales_billing', parentPermission: 'sales' },
          { name: 'Sales Returns', icon: RotateCcw, path: '/sales-returns', permissionId: 'sales_billing', parentPermission: 'sales' },
          { name: 'Customer Ledger', icon: BookOpen, path: '/customer-ledger', permissionId: 'sales_billing', parentPermission: 'sales' },
        ]
      },
      { 
        name: 'Contracts', 
        icon: FileText, 
        path: '/contracts', 
        permissionId: 'contract_management',
        parentPermission: 'contracts'
      },
      { 
        name: 'Stamping/License', 
        icon: Award, 
        path: '/licenses', 
        permissionId: 'licenses',
        parentPermission: 'contracts'
      },
      // Invoices module removed - handled in Sales module
      { 
        name: 'Inventory', 
        icon: Warehouse, 
        permissionId: 'inventory',
        parentPermission: 'inventory',
        subPermissions: ['purchase_entry', 'stock_list', 'serial_tracker'],
        subItems: [
          { name: 'Purchase Entry', icon: ShoppingCart, path: '/inventory/purchases', permissionId: 'purchase_entry', parentPermission: 'inventory' },
          { name: 'Stock List', icon: Package, path: '/inventory/stock', permissionId: 'stock_list', parentPermission: 'inventory' },
          { name: 'Serial Tracker', icon: Search, path: '/inventory/serial-tracker', permissionId: 'serial_tracker', parentPermission: 'inventory' },
        ]
      },
      { 
        name: 'Expenses', 
        icon: Receipt, 
        path: '/expenses', 
        permissionId: 'expenses',
        parentPermission: 'finance'
      },
      { 
        name: 'Suppliers', 
        icon: Truck, 
        permissionId: 'finance',
        parentPermission: 'finance',
        subPermissions: ['supplier_payments', 'supplier_ledger'],
        subItems: [
          { name: 'Supplier Payments', icon: Wallet, path: '/supplier-payments', permissionId: 'supplier_payments', parentPermission: 'finance' },
          { name: 'Supplier Ledger', icon: BookOpen, path: '/supplier-ledger', permissionId: 'supplier_ledger', parentPermission: 'finance' },
        ]
      },
      { 
        name: 'Attendance', 
        icon: Clock, 
        path: '/attendance', 
        permissionId: 'attendance',
        parentPermission: 'hr'
      },
      { 
        name: 'Settings', 
        icon: Settings, 
        permissionId: 'masters',
        parentPermission: 'masters',
        subPermissions: ['business_settings', 'roles_master', 'employees', 'brand_master', 'category_master', 'product_master', 'tax_settings', 'supplier_master', 'expense_headers', 'attendance_settings'],
        subItems: [
          { name: 'Business Settings', icon: Building2, path: '/settings/business', permissionId: 'business_settings', parentPermission: 'masters' },
          { name: 'Billing Settings', icon: Receipt, path: '/settings/billing', permissionId: 'business_settings', parentPermission: 'masters' },
          { name: 'Database Backup', icon: CloudUpload, path: '/settings/backup', permissionId: 'business_settings', parentPermission: 'masters' },
          { name: 'Roles Master', icon: Shield, path: '/settings/roles', permissionId: 'roles_master', parentPermission: 'masters' },
          { name: 'Employee Master', icon: Users, path: '/settings/employees', permissionId: 'employees', parentPermission: 'hr' },
          { name: 'Brand Master', icon: Tag, path: '/settings/brands', permissionId: 'brand_master', parentPermission: 'masters' },
          { name: 'Category Master', icon: FolderTree, path: '/settings/categories', permissionId: 'category_master', parentPermission: 'masters' },
          { name: 'Product Master', icon: Package, path: '/settings/products', permissionId: 'product_master', parentPermission: 'masters' },
          { name: 'Tax Settings', icon: Percent, path: '/settings/taxes', permissionId: 'tax_settings', parentPermission: 'masters' },
          { name: 'Supplier Master', icon: Truck, path: '/settings/suppliers', permissionId: 'supplier_master', parentPermission: 'masters' },
          { name: 'Expense Headers', icon: Receipt, path: '/settings/expense-headers', permissionId: 'expense_headers', parentPermission: 'masters' },
          { name: 'Attendance Settings', icon: CalendarClock, path: '/settings/attendance', permissionId: 'attendance_settings', parentPermission: 'hr' }
        ]
      },
    ];

    // Filter items based on permissions - ONLY check direct permissions
    const filteredItems = allItems.filter(item => {
      // Always show items marked as alwaysShow
      if (item.alwaysShow) return true;
      
      // Admin sees everything
      if (isAdmin) return true;
      
      // Check if user has permission for this item
      if (item.subItems && item.subPermissions) {
        // For items with sub-menus, show if user has permission for any sub-item
        return hasAnyPermissionInModule(item.subPermissions);
      }
      
      // Check direct permission only
      return hasPermission(item.permissionId, 'view');
    });

    // Filter sub-items based on permissions
    return filteredItems.map(item => {
      if (item.subItems && !isAdmin) {
        return {
          ...item,
          subItems: item.subItems.filter(sub => hasPermission(sub.permissionId, 'view'))
        };
      }
      return item;
    }).filter(item => {
      // Remove parent items with no sub-items (after filtering)
      if (item.subItems && item.subItems.length === 0) return false;
      return true;
    });
  };

  const navigationItems = getNavigationItems();

  const isActive = (path) => location.pathname === path;
  const isSubItemActive = (subItems) => subItems?.some(sub => location.pathname === sub.path);

  const toggleSubmenu = (menuName) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 fixed w-full top-0 z-50 shadow-sm">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                if (window.innerWidth >= 1024) {
                  setSidebarCollapsed(!sidebarCollapsed);
                } else {
                  setSidebarOpen(!sidebarOpen);
                }
              }}
              className="text-gray-600 hover:text-gray-900"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <div className="flex items-center space-x-3">
              {businessInfo?.logo_url ? (
                <img 
                  src={businessInfo.logo_url} 
                  alt="Logo" 
                  className="h-10 w-auto object-contain"
                />
              ) : (
                <div className="h-10 w-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">{getInitials(businessInfo?.business_name)}</span>
                </div>
              )}
              <div>
                <h1 className="text-lg font-bold text-gray-900">{businessInfo?.business_name || 'SUNTECH ENTERPRISES, Coimbatore'}</h1>
                <p className="text-xs text-gray-500">Management System</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Check-in/Check-out Button */}
            <CheckInButton />
            
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center space-x-2">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-blue-600 text-white">
                    {user?.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-gray-500">{user?.role}</p>
                </div>
                <ChevronDown size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div>
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                  <p className="text-xs text-blue-600 font-medium mt-1">{user?.role}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setChangePasswordOpen(true)} className="cursor-pointer">
                <Key size={16} className="mr-2" />
                Change Password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                <LogOut size={16} className="mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex pt-16">
        {/* Sidebar */}
        <aside
          className={`${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-40 ${
            sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'
          } w-64 bg-white border-r border-gray-200 transition-all duration-300 ease-in-out mt-16`}
        >
          <nav className="p-4 space-y-1">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              
              // If item has subItems, render as expandable menu
              if (item.subItems) {
                const isExpanded = expandedMenus[item.name] || isSubItemActive(item.subItems);
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => !sidebarCollapsed && toggleSubmenu(item.name)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                        isSubItemActive(item.subItems)
                          ? 'bg-blue-50 text-blue-600'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                      title={sidebarCollapsed ? item.name : ''}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon size={20} />
                        {!sidebarCollapsed && <span className="font-medium">{item.name}</span>}
                      </div>
                      {!sidebarCollapsed && (
                        <ChevronRight 
                          size={16} 
                          className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        />
                      )}
                    </button>
                    {!sidebarCollapsed && isExpanded && (
                      <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-3">
                        {item.subItems.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const subActive = isActive(subItem.path);
                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => setSidebarOpen(false)}
                              className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors text-sm ${
                                subActive
                                  ? 'bg-blue-600 text-white'
                                  : 'text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              <SubIcon size={16} />
                              <span>{subItem.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              // Regular menu item
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    active
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  title={sidebarCollapsed ? item.name : ''}
                >
                  <Icon size={20} />
                  {!sidebarCollapsed && <span className="font-medium">{item.name}</span>}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className={`flex-1 p-6 lg:p-8 transition-all duration-300 ${
          sidebarCollapsed ? 'lg:ml-0' : 'lg:ml-0'
        }`}>
          <Outlet />
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Change Password Dialog */}
      <Dialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Change Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="current_password">Current Password *</Label>
              <Input
                id="current_password"
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                placeholder="Enter current password"
              />
            </div>
            <div>
              <Label htmlFor="new_password">New Password *</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                placeholder="Enter new password (min 6 characters)"
              />
            </div>
            <div>
              <Label htmlFor="confirm_password">Confirm New Password *</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                placeholder="Confirm new password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setChangePasswordOpen(false);
              setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
            }}>
              Cancel
            </Button>
            <Button onClick={handleChangePassword} disabled={changingPassword}>
              {changingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
